import { createColumnHelper } from '@tanstack/react-table';
import { ArchiveIcon, CircleCheckBigIcon, FilePenLineIcon, type LucideIcon } from 'lucide-react';
import * as z from 'zod';

import {
  ListingStatus,
  ListingStrategy,
  type ListingDTO,
} from '@invenco/sales-interface/interfaces';

import { Card, CardHeader } from '@/components/core/card';
import {
  DataTableCardBody,
  DataTableCardTitle,
  EditableDataTable,
  useDataTableState,
  useEditableCellDataTable,
} from '@/components/data-table';
import { StatusBadge, type StatusType } from '@/components/status-badge';
import { useGateways } from '@/gateways/GatewayProvider';
import { cn, formatNumber } from '@/shared/helpers';
import { useGatewayMutation, useQueryWithInput } from '@/shared/hooks/queries';
import { listingStatusType } from '@/shared/status-maps';
import { listingStatusTitle, listingStrategyTitle } from '@/shared/title-maps';

type Props = {
  skuId?: string;
};

const columnHelper = createColumnHelper<ListingDTO>();

const editableListingStatuses = [
  ListingStatus.ACTIVE,
  ListingStatus.DRAFT,
  ListingStatus.ARCHIVED,
] as const;

const editableListingStrategies = [
  ListingStrategy.DYNAMIC_AVAILABLE,
  ListingStrategy.PRESELL,
  ListingStrategy.SOLD_OUT,
] as const;

const listingStatusIcon: Record<ListingStatus, LucideIcon> = {
  [ListingStatus.ACTIVE]: CircleCheckBigIcon,
  [ListingStatus.DRAFT]: FilePenLineIcon,
  [ListingStatus.ARCHIVED]: ArchiveIcon,
};

const listingStatusIconClassName = {
  success: 'text-state-success-foreground',
  progress: 'text-state-progress-foreground',
  observe: 'text-state-observe-foreground',
  alert: 'text-state-alert-foreground',
  inactive: 'text-state-inactive-foreground',
} as const satisfies Record<NonNullable<StatusType>, string>;

const editableListingColumnIds = ['externalSkuName', 'status', 'strategy'] as const;

type EditableListingColumnId = (typeof editableListingColumnIds)[number];
type ListingPatch = Pick<ListingDTO, 'id'> & Partial<ListingDTO>;

const listingEditableSchema = z.object({
  externalSkuName: z.string().trim(),
  status: z.enum(editableListingStatuses),
  strategy: z.enum(editableListingStrategies),
});

function isEditableListingColumnId(columnId: string): columnId is EditableListingColumnId {
  return (editableListingColumnIds as readonly string[]).includes(columnId);
}

function renderListingStatusOptionLabel(status: ListingStatus) {
  const Icon = listingStatusIcon[status];
  const statusType = listingStatusType[status] ?? 'inactive';

  return (
    <span className="flex items-center gap-2">
      <Icon aria-hidden className={cn('size-4 shrink-0', listingStatusIconClassName[statusType])} />
      <span>{listingStatusTitle[status] || status}</span>
    </span>
  );
}

const columns = [
  columnHelper.accessor('channelName', {
    header: 'Channel',
  }),
  columnHelper.accessor('externalSkuName', {
    header: 'Listed SKU',
    cell: ({ getValue, row }) => {
      const listedSkuName = getValue()?.trim();
      const content = listedSkuName || row.original.skuName || '';

      return (
        <StatusBadge
          className="max-w-full justify-start"
          type={listedSkuName ? 'progress' : 'inactive'}
        >
          <span className="block truncate">{content}</span>
        </StatusBadge>
      );
    },
    meta: {
      editable: {
        label: 'Listed SKU',
        tooltip: (value) => (value ? 'Update listed SKU mapping' : 'Map to listed SKU'),
        editor: {
          type: 'text',
        },
      },
    },
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => (
      <StatusBadge status={getValue()} type={listingStatusType} title={listingStatusTitle} />
    ),
    meta: {
      editable: {
        label: 'Status',
        editor: {
          type: 'select',
          placeholder: 'Select status',
          options: editableListingStatuses.map((status) => ({
            value: status,
            textLabel: listingStatusTitle[status] || status,
            label: renderListingStatusOptionLabel(status),
          })),
          renderTrigger: (value) => (
            <StatusBadge
              status={value as ListingStatus}
              type={listingStatusType}
              title={listingStatusTitle}
            />
          ),
        },
      },
    },
  }),
  columnHelper.accessor('strategy', {
    header: 'Strategy',
    cell: ({ getValue }) => {
      const strategy = getValue();
      return strategy ? listingStrategyTitle[strategy] : null;
    },
    meta: {
      editable: {
        label: 'Strategy',
        editor: {
          type: 'select',
          placeholder: 'Select strategy',
          options: editableListingStrategies.map((strategy) => ({
            value: strategy,
            label: listingStrategyTitle[strategy] || strategy,
          })),
        },
      },
    },
  }),
  columnHelper.accessor('qtyExpected', {
    header: 'Expected',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
  columnHelper.accessor('qtyAvailable', {
    header: 'Available',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
  columnHelper.accessor('qtyListed', {
    header: 'Listed',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
];

export function ListingsTable({ skuId }: Props) {
  const { salesGateway } = useGateways();

  const listingsInput = { skuIds: [skuId!] };
  const listingsQueryKey = ['listings', listingsInput] as const;
  const { data, isLoading, isError } = useQueryWithInput({
    parentKey: 'listings',
    input: listingsInput,
    query: (input, { signal }) => salesGateway.getListings(input, { signal }),
    enabled: Boolean(skuId),
  });

  const { mutate: updateListing } = useGatewayMutation<ListingDTO, ListingPatch>({
    mutationFn: (patch) => salesGateway.updateListing(patch),
    linkedQuery: listingsQueryKey,
    linkResponseToQuery: false,
  });

  const { state, onSortingChange } = useDataTableState({
    columns,
    tableId: 'sku-details-listings',
    defaultPageSize: 10,
  });

  const { table, ...tableProps } = useEditableCellDataTable({
    data: data?.items ?? [],
    columns,
    schema: listingEditableSchema,
    isLoading,
    hasError: isError,
    manualPagination: true,
    manualSorting: false,
    state,
    onSortingChange,
    onCommitCell: async ({ rowId, columnId, value }) => {
      if (!isEditableListingColumnId(columnId)) {
        throw new Error(`Unsupported editable listing column: ${columnId}`);
      }

      return updateListing({
        id: rowId,
        [columnId]: value as ListingDTO[typeof columnId],
      } satisfies ListingPatch);
    },
  });

  return (
    <Card aria-label="Listings">
      <CardHeader>
        <DataTableCardTitle table={table}>Listings</DataTableCardTitle>
      </CardHeader>
      <DataTableCardBody table={table}>
        <EditableDataTable table={table} {...tableProps} />
      </DataTableCardBody>
    </Card>
  );
}
